"use client"

import { ArrowRight, ChevronRightMini } from "@medusajs/icons"
import { useState } from "react"
import { useProductPurchaseIntent } from "../../hooks/use-product-purchase-intent"
import type { StoreFaqSection, StoreProductMedia } from "../../types"
import SafeRichText from "@modules/common/components/safe-rich-text"
import styles from "./product-page-sections.module.css"

type FaqProps = {
  section: StoreFaqSection
}

const FaqMedia = ({ media }: { media?: StoreProductMedia | null }) => {
  if (!media) return null

  if (media.type === "video") {
    return (
      <video
        className={styles.faqImage}
        src={media.url}
        poster={media.poster_url ?? undefined}
        preload="none"
        controls
        aria-label={media.alt_text ?? "SSS videosu"}
      />
    )
  }

  return (
    <img
      className={styles.faqImage}
      src={media.url}
      alt={media.alt_text ?? ""}
      loading="lazy"
    />
  )
}

export default function Faq({ section }: FaqProps) {
  const purchaseIntent = useProductPurchaseIntent("product-purchase-panel")
  const [openIndex, setOpenIndex] = useState<number | null>(null)
  const { content } = section
  const sectionId = section.id ?? content.id ?? String(section.position ?? 0)
  const items = [...content.items].sort(
    (left, right) => (left.position ?? 0) - (right.position ?? 0)
  )
  const actionLabel = content.action_label?.trim()

  const actionContent = actionLabel ? (
    <span>
      <span className={styles.faqButtonLabel}>{actionLabel}</span>
      <span className={styles.faqButtonIcon} aria-hidden="true">
        <ArrowRight />
      </span>
    </span>
  ) : null

  return (
    <section className={styles.faqSection} aria-label={content.title}>
      <div className={styles.faqInner}>
        {content.image_media ? (
          <div className={styles.faqMedia}>
            <FaqMedia media={content.image_media} />
          </div>
        ) : null}

        <div className={styles.faqContent}>
          <header className={styles.faqHeader}>
            <h2 className={styles.faqTitle}>{content.title}</h2>
            {content.subtitle ? <p className={styles.faqSubtitle}>{content.subtitle}</p> : null}
            <SafeRichText document={content.body} />
          </header>

          <div className={styles.faqList}>
            {items.map((item, index) => {
              const isOpen = openIndex === index
              const itemId = item.id ?? String(index)
              const triggerId = `product-faq-${sectionId}-trigger-${itemId}`
              const panelId = `product-faq-${sectionId}-panel-${itemId}`

              return (
                <div
                  key={item.id ?? `${item.position ?? index}-${item.question}`}
                  className={styles.faqItem}
                  data-faq-item={index}
                  data-open={isOpen ? "true" : "false"}
                >
                  <button
                    id={triggerId}
                    type="button"
                    className={styles.faqTrigger}
                    aria-expanded={isOpen}
                    aria-controls={panelId}
                    onClick={() => setOpenIndex((current) => (current === index ? null : index))}
                  >
                    <span>{item.question}</span>
                    <ChevronRightMini className={styles.faqIcon} aria-hidden="true" />
                  </button>
                  <div
                    id={panelId}
                    role="region"
                    aria-label={item.question}
                    aria-labelledby={triggerId}
                    aria-hidden={!isOpen}
                    className={styles.faqPanel}
                  >
                    <div className={styles.faqPanelInner}>
                      <SafeRichText document={item.answer} />
                    </div>
                  </div>
                </div>
              )
            })}
          </div>

          {actionLabel && content.action_type === "add_to_cart" ? (
            <button
              type="button"
              className={styles.faqButton}
              disabled={purchaseIntent.disabled}
              onClick={purchaseIntent.run}
            >
              {actionContent}
            </button>
          ) : null}

          {actionLabel && content.action_type === "link" && content.action_href ? (
            <a className={styles.faqButton} href={content.action_href}>
              {actionContent}
            </a>
          ) : null}
        </div>
      </div>
    </section>
  )
}
